/**
 * siteMapTreatment — Phase 4 (R-144). Pure resolver that turns a report
 * `theme.siteMapTreatment` (+ the preset IDENTITY, supplied as `presetId`) into
 * the concrete figure-framing presentation:
 *   - the caption prefix ("Figure N.", "Fig. N ·", or none),
 *   - whether an EXHIBIT header bar renders ("EXHIBIT N — SITE OVERVIEW"),
 *   - whether an accent top-rule is drawn (Monograph),
 *   - the swatch/label colour TONE for the chips on this figure.
 *
 * RC4 (report-layout-rebuild W1): identity is now taken from the un-merged
 * preset id (`presentation.presetId`) — NOT inferred from a `/spectral/i` font
 * sniff — so a tenant font override can no longer silently revert the Monograph
 * framing. The caption LABEL comes from `theme.figureNumberingPrefix` (so a
 * tenant's custom prefix like "Exhibit " reaches the headline site-map figure);
 * the PRESET only chooses the SEPARATOR style (Monograph = abbreviated mid-dot
 * "Fig. N ·"; every other framed preset = "<Label> N.") and the accent rule.
 *
 * Back-compat: when `siteMapTreatment` is ABSENT (legacy / no-preset reports),
 * this resolves to the `'framed-figure'` behaviour WITHOUT a numbered caption
 * prefix and WITHOUT an accent rule, i.e. today's plain framed map — so legacy
 * reports are visually unchanged. Only a preset (or explicit user choice) opts
 * a report into the numbered/exhibit chrome.
 *
 * Pure / side-effect-free so it is safe in render and unit-testable.
 */
import type { ReportTheme } from '../types/reportTheme';
import type { LayerColorTone } from '../config/siteLayerColors';
import { resolveFigureNumberingPrefix } from './resolveReportPresentation';

export type SiteMapTreatment = NonNullable<ReportTheme['siteMapTreatment']>;

export interface ResolvedSiteMapFigure {
  /** Effective treatment after defaulting. */
  treatment: SiteMapTreatment;
  /** EXHIBIT header bar text, or null when not an exhibit treatment. */
  exhibitHeader: string | null;
  /** Figure caption prefix incl. the number + separator, or null when the
   *  treatment doesn't number figures (legacy framed map / full-bleed). */
  captionPrefix: string | null;
  /** Draw the Monograph accent top-rule above the figure frame. */
  accentTopRule: boolean;
  /** Render the bordered figure frame (false only for full-bleed). */
  framed: boolean;
  /** Colour tone the figure chips/labels should use. Professional presets are
   *  all LIGHT pages → `'on-light'`; legacy/full-bleed keep `'on-dark'`. */
  tone: LayerColorTone;
}

/**
 * Trim a figure-numbering prefix down to its bare LABEL word (drop the trailing
 * space and any trailing punctuation the prefix carried). "Figure " → "Figure";
 * "Exhibit " → "Exhibit"; "Fig. " → "Fig.".
 */
function figureLabel(prefix: string): string {
  return prefix.trim().replace(/[.·\-\s]+$/u, '').trim();
}

/**
 * Monograph abbreviation: only the canonical "Figure" label abbreviates to
 * "Fig." (preserving the approved Monograph preview + the existing parity
 * tests). A tenant's custom label (e.g. "Exhibit") keeps its full word and
 * still gets the Monograph mid-dot separator + accent rule.
 */
function monographLabel(label: string): string {
  return /^figure$/i.test(label) ? 'Fig.' : label;
}

/**
 * resolveSiteMapFigure — resolve the figure framing for the site-overview map.
 *
 * @param theme       reads `siteMapTreatment` (which framing) +
 *                    `figureNumberingPrefix` (caption label). `headingFont` is
 *                    retained ONLY as a back-compat fallback for the Monograph
 *                    identity when no `presetId` is supplied.
 * @param figureNumber 1-based figure number within the report. Defaults to 1
 *                    (the site map is the canonical first figure).
 * @param presetId    the un-merged theme preset id (`presentation.presetId`).
 *                    Authoritative for the Monograph framing — supersedes the
 *                    legacy `/spectral/i` font sniff. Absent ⇒ fall back to the
 *                    font heuristic so existing callers keep working.
 */
export function resolveSiteMapFigure(
  theme: Pick<ReportTheme, 'siteMapTreatment' | 'figureNumberingPrefix' | 'headingFont'>,
  figureNumber = 1,
  presetId?: string | null,
): ResolvedSiteMapFigure {
  const explicit = theme.siteMapTreatment;

  // Legacy / no-preset: today's plain framed map, NO numbering, NO accent rule,
  // dark-map tone. Byte-stable with the pre-Phase-4 render.
  if (!explicit) {
    return {
      treatment: 'framed-figure',
      exhibitHeader: null,
      captionPrefix: null,
      accentTopRule: false,
      framed: true,
      tone: 'on-dark',
    };
  }

  if (explicit === 'exhibit') {
    return {
      treatment: 'exhibit',
      exhibitHeader: `EXHIBIT ${figureNumber} — SITE OVERVIEW`,
      captionPrefix: null,
      accentTopRule: false,
      framed: true,
      tone: 'on-light',
    };
  }

  if (explicit === 'full-bleed') {
    return {
      treatment: 'full-bleed',
      exhibitHeader: null,
      captionPrefix: null,
      accentTopRule: false,
      framed: false,
      // Full-bleed edge-to-edge maps sit on the (dark) imagery, not the page.
      tone: 'on-dark',
    };
  }

  // framed-figure (explicit). The Monograph variant ("Fig. N ·" + accent
  // top-rule) is selected by the PRESET ID (RC4) — not the heading font. When
  // no presetId is supplied we fall back to the historical /spectral/i sniff so
  // legacy callers are unaffected. The caption LABEL comes from
  // `figureNumberingPrefix`; the preset only chooses the separator + accent.
  const isMonograph = presetId != null
    ? presetId === 'monograph'
    : /spectral/i.test(theme.headingFont ?? '');
  const label = figureLabel(resolveFigureNumberingPrefix(theme));
  const captionPrefix = isMonograph
    ? `${monographLabel(label)} ${figureNumber} · `
    : `${label} ${figureNumber}. `;
  return {
    treatment: 'framed-figure',
    exhibitHeader: null,
    captionPrefix,
    accentTopRule: isMonograph,
    framed: true,
    tone: 'on-light',
  };
}
